<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import SelectButton from 'primevue/selectbutton'
import AutoComplete from 'primevue/autocomplete'
import { useToast } from 'primevue/usetoast'
import { useSupportStore } from '../stores/supportStore'
import { supportApi, type SearchResult } from '../services/supportApi'
import { capturePageContext } from '../lib/pageContext'

const store = useSupportStore()
const route = useRoute()
const toast = useToast()
const submitting = ref(false)

const description = ref('')
const requestType = ref<'bug' | 'enhancement'>('bug')
const priority = ref<'low' | 'medium' | 'high'>('medium')

const selectedLink = ref<SearchResult | null>(null)
const linkSuggestions = ref<SearchResult[]>([])

async function searchLinks(e: { query: string }) {
  if (e.query.trim().length < 2) { linkSuggestions.value = []; return }
  try {
    const all = await supportApi.search(e.query)
    linkSuggestions.value = all.filter((r) => ['Driver', 'Applicant', 'Client'].includes(r.type))
  } catch { linkSuggestions.value = [] }
}

function resolvedLink(): { related_client_id: string | null; related_employee_id: string | null } {
  const s = selectedLink.value
  if (s && (s.type === 'Driver' || s.type === 'Applicant')) {
    return { related_employee_id: s.id, related_client_id: s.client_id ?? null }
  }
  if (s && s.type === 'Client') {
    return { related_employee_id: null, related_client_id: s.id }
  }
  return {
    related_employee_id: store.prefill.related_employee_id ?? null,
    related_client_id: store.prefill.related_client_id ?? null,
  }
}

const open = computed({
  get: () => store.dialogOpen,
  set: (v) => { if (!v) store.closeRequest() },
})

const types = [
  { label: 'Bug', value: 'bug' },
  { label: 'Enhancement', value: 'enhancement' },
]
const priorities = [
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' },
]

watch(() => store.dialogOpen, (isOpen) => {
  if (!isOpen) return
  requestType.value = store.prefill.request_type
  description.value = store.prefill.description
  priority.value = 'medium'
  selectedLink.value = store.prefill.link_label
    ? ({
        type: store.prefill.related_employee_id ? 'Driver' : 'Client',
        id: store.prefill.related_employee_id ?? store.prefill.related_client_id ?? '',
        label: store.prefill.link_label,
        sub: '',
        client_id: store.prefill.related_client_id ?? null,
      } as SearchResult)
    : null
})

const ctxChips = computed(() => {
  const c: string[] = []
  if (route.params.clientId || route.params.id) c.push('Client context attached')
  if (route.params.employeeId) c.push('Driver context attached')
  if (store.prefill.page_context?.triggering_error) c.push('Error details attached')
  c.push(`Page: ${document.title}`)
  return c
})

async function submit() {
  if (description.value.trim().length < 5) {
    toast.add({ severity: 'warn', summary: 'Add a bit more detail', life: 3000 })
    return
  }
  submitting.value = true
  const page_context = store.prefill.page_context
    ?? capturePageContext(route, { recentErrors: store.errorLog })
  const link = resolvedLink()
  const ok = await store.submit({
    request_type: requestType.value,
    description: description.value.trim(),
    priority: priority.value,
    page_context,
    related_client_id: link.related_client_id ?? page_context.related_client_id,
    related_employee_id: link.related_employee_id ?? page_context.related_employee_id,
  })
  submitting.value = false
  if (ok) {
    toast.add({ severity: 'success', summary: 'Submitted', detail: "We'll track this and follow up.", life: 5000 })
    store.closeRequest()
  } else {
    toast.add({ severity: 'error', summary: 'Failed to submit', detail: store.error ?? 'Try again', life: 5000 })
  }
}
</script>

<template>
  <PDialog v-model:visible="open" header="Report a problem / request a change" :style="{ width: '480px' }" modal :closable="!submitting">
    <div class="flex flex-col gap-4">
      <div class="flex flex-wrap gap-2">
        <PTag v-for="c in ctxChips" :key="c" :value="c" severity="info" />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium">Link a driver or client (optional)</label>
        <AutoComplete
          v-model="selectedLink"
          :suggestions="linkSuggestions"
          optionLabel="label"
          placeholder="Search a driver or client…"
          @complete="searchLinks"
          dropdown
          class="w-full"
        >
          <template #option="{ option }">
            <div class="flex flex-col">
              <span class="text-sm">{{ option.label }} <span class="text-secondary-400">· {{ option.type }}</span></span>
              <span v-if="option.sub" class="text-xs text-secondary-500">{{ option.sub }}</span>
            </div>
          </template>
        </AutoComplete>
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium">Type</label>
        <SelectButton v-model="requestType" :options="types" optionLabel="label" optionValue="value" />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium">{{ requestType === 'bug' ? "What went wrong?" : "What would you like?" }}</label>
        <PTextarea v-model="description" rows="4" class="w-full" autofocus
          :placeholder="requestType === 'bug' ? 'What happened? What did you expect?' : 'Describe the enhancement...'" />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium">Priority</label>
        <SelectButton v-model="priority" :options="priorities" optionLabel="label" optionValue="value" />
      </div>
    </div>
    <template #footer>
      <PButton label="Cancel" text @click="store.closeRequest()" :disabled="submitting" />
      <PButton label="Submit" icon="pi pi-send" @click="submit" :loading="submitting" />
    </template>
  </PDialog>
</template>
